import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, CATEGORY } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const defaultColSpan = { md: 6 };

const CategoryForm = NiceModal.create(({ id, mode, modalName }) => {
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const { showSuccess, showError } = useAlert();
	const { editMode, viewMode } = useMemo(
		() => ({
			editMode: mode === 'edit',
			viewMode: mode === 'view'
		}),
		[mode]
	);
	const { LanguageCode } = useLocale();

	const CategoryAutocompleteColumns = {
		trValue: [{ accessor: 'NameTr', width: 200 }],
		engValue: [{ accessor: 'NameEn', width: 200 }],
		arabicValue: [{ accessor: 'NameAr', width: 200 }]
	};

	const schema = useMemo(
		() =>
			yup.object().shape({
				nameEn: yup.string().required(),
				nameAr: yup.string().nullable(),
				nameTr: yup.string().nullable(),
				parentId: yup.number().nullable()
			}),
		[]
	);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = useCallback(async () => {
		const defaultValues = {
			nameEn: null,
			nameAr: null,
			nameTr: null,
			parentId: null
		};
		if (!id) return defaultValues;
		try {
			const { data, status } = await axiosPrivate.post(CATEGORY.GET_CATEGORY, { categoryId: id });
			if (status === 200) {
				return {
					nameEn: data.nameEn ?? null,
					nameAr: data.nameAr ?? null,
					nameTr: data.nameTr ?? null,
					parentId: data.parentId ?? null
				};
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error.response?.data?.code || error?.response?.data?.message
			});
			return defaultValues;
		}
	}, [id]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);
	const dispatch = useDispatch();
	const onSubmit = (data) => {
		const URL = id ? CATEGORY.UPDATE_CATEGORY : CATEGORY.ADD_CATEGORY;
		axiosPrivate
			.post(URL, {
				...data,
				categoryId: id
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.PRODUCTS_CATEGORIES));
					handleCloseModal();
				}
			})
			.catch((error) => {
				showError({
					message: error.response?.data?.code || error?.response?.data?.message
				});
			});
	};

	return (
		<Modal fullwidth maxWidth="medium" name={modalName} className="modal-category">
			<ModalTitle>
				<Typography variant="p1">
					{editMode ? 'editCategory' : viewMode ? 'viewCategory' : 'addCategory'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="nameEn" required isLoading={isLoading}>
						<StringInput name="nameEn" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="nameAr" isLoading={isLoading}>
						<StringInput name="nameAr" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="nameTr" isLoading={isLoading}>
						<StringInput name="nameTr" disabled={viewMode} />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="parentCategory" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.PRODUCT_CATEGORIES}
							columns={CategoryAutocompleteColumns[LanguageCode]}
							selectionLabelAccessor={[CategoryAutocompleteColumns[LanguageCode][0].accessor]}
							value={watch('parentId')}
							setValue={(value) => {
								setValue('parentId', value?.ID ?? null, { shouldDirty: true, shouldValidate: true });
							}}
							disabled={isLoading}
							name="parentId"
						/>
					</FormItem>
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					<Button
						loading={isLoading}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						label={editMode ? 'save' : 'button.add'}
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default CategoryForm;
